import { useRef, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { VerifyEmailOtpModal } from "../../components/features/onboard-verify/modals/verify-email-otp-modal.tsx";
import { usePrejoinOtp } from "../../components/features/onboard-verify/hooks/use-prejoin-otp.ts";
import { resolveOnboardingDestination, type OnboardingDestination } from "../../components/features/onboard-verify/schemas/onboarding-destination.ts";
import { VerifiedOnboardingContext, type VerifiedOnboarding } from "../../components/features/onboard-verify/verified-onboarding-context.ts";
import CreateEmployeePage from "../onboard-pre-join/create-employee-page.tsx";
import UsPreJoinPage from "./us/pre-join/us-pre-join-page.tsx";
import ConsultantIndiaPage from "./consultant-india/consultant-india-page.tsx";
import ConsultantUsPage from "./consultant-us/consultant-us-page.tsx";
import ConsultantAgencyUsPage from "./consultant-agency-us/consultant-agency-us-page.tsx";
import AgencyRegistrationPage from "./agency-registration/agency-registration-page.tsx";
import styles from "../onboard-pre-join/create-employee-page.module.css";
import { SqeOnboardingLinkContext, useSqeOnboardingApi } from "../../components/features/onboarding/sqe/sqe-onboarding-link-context.tsx";

const FORM_PAGES = {
  "regular-india": CreateEmployeePage,
  "regular-us": UsPreJoinPage,
  "consultant-india": ConsultantIndiaPage,
  "consultant-us": ConsultantUsPage,
  "consultant-agency-us": ConsultantAgencyUsPage,
  agency: AgencyRegistrationPage,
};

function OnboardingLinkSession({ token, regularUsLink }: { token: string; regularUsLink: boolean }) {
  const isSqe = useSqeOnboardingApi();
  const { generateOtp, validateOtp } = usePrejoinOtp();
  const [verified, setVerified] = useState<(VerifiedOnboarding & { destination: OnboardingDestination }) | null>(null);
  const generation = useRef(0);
  if (verified) {
    const Page = FORM_PAGES[verified.destination];
    return <VerifiedOnboardingContext.Provider value={verified}><Page /></VerifiedOnboardingContext.Provider>;
  }
  return (
    <div className={styles.page} data-theme="caelius-classic">
      <header className={styles.topBar}><img className={`${styles.logo} ${isSqe ? styles.sqeLogo : ""}`.trim()} src={isSqe ? "/assets/sqe-login/sqe_logo.svg" : "/assets/images/logo.svg"} alt={isSqe ? "SQE Labs" : "Caelius Consulting"} width={isSqe ? 110 : 150} /></header>
      <main className={styles.main}><div className={styles.inner}>
        <h1 className="heading-lg">Verify your onboarding invitation</h1>
        {token ? <VerifyEmailOtpModal
          open
          token={token}
          variant="invitation"
          onGenerateOtp={(email) => { generation.current += 1; return generateOtp(token, email); }}
          onValidate={async (email, otp) => {
            const requestGeneration = generation.current;
            const body = await validateOtp(token, email, otp);
            const { details, destination } = resolveOnboardingDestination(body, regularUsLink);
            if (details.email && details.email.trim().toLowerCase() !== email.trim().toLowerCase()) throw new Error("The verified email does not match this invitation.");
            if (requestGeneration !== generation.current) return;
            setVerified({ token, email: details.email ?? email.trim(), firstName: details.firstName ?? undefined, lastName: details.lastName ?? undefined, onboardingReferenceId: details.onboardingReferenceId, destination });
          }}
        /> : <p className={styles.error} role="alert">This invitation link is missing its token. Open the complete link from your invitation email.</p>}
      </div></main>
    </div>
  );
}

export default function OnboardingLinkPage() {
  const [params] = useSearchParams();
  const { pathname } = useLocation();
  const token = params.get("token")?.trim() ?? "";
  return <SqeOnboardingLinkContext.Provider value={pathname === "/create-employee/sqe"}>
    <OnboardingLinkSession key={`${pathname}:${token}`} token={token} regularUsLink={pathname === "/create-employee/us" || pathname === "/onboarding/us/pre-join"} />
  </SqeOnboardingLinkContext.Provider>;
}
